'use client';

import React, { useEffect, useMemo, useSyncExternalStore } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { Price } from '@/components/Price';
import { ProductCard } from '@/components/ProductCard';
import { ProductCardLike } from '@/components/ProductCard/ProductCardLike';
import { ScrollableItemRow } from '@/components/ScrollableItemRow';
import { SecondaryPriceLine } from '@/components/SecondaryPriceLine';
import {
  interactionTypes,
  ActivityTrackerEventType,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { picturePreviewAdapter } from '@/modules/pictures/helpers';
import { type ProductBySlugResponse } from '@/modules/product/aliases';
import { IS_PLA_REFERER } from '@/modules/product/constants';
import { getSizeString } from '@/modules/product/helpers/getSizeString';
import { useBespokeCondensedPDP } from '@/modules/product/hooks/useBespokeCondensedPDP';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking';
import { type SimilarProduct } from '@/modules/similar/aliases';
import { getSimilarSashAndYMALProducts } from '@/modules/similar/helpers';
import { getSessionStorageItem } from '@/modules/storage';

type Props = {
  products: SimilarProduct[];
  productId: ProductBySlugResponse['id'];
  title: string;
  colourId?: string;
};

export function SimilarItemsSashUI({
  productId,
  products,
  title,
  colourId,
}: Props) {
  const t = useTranslations('common');
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const { sendActivityTrackerEvent } = useActivityTracker();

  const isPLAUser = useSyncExternalStore(
    () => {
      // sessionStorage doesn't emit events, so we return a no-op unsubscribe function
      return () => undefined;
    },
    () => getSessionStorageItem(IS_PLA_REFERER) === 'true', // client-side value
    () => false // SSR value
  );

  const { similarSashCount: sashCount, isVariant: isVariantSash } =
    useBespokeCondensedPDP();

  const { similarSash: similarSashProducts, colourMatchesCount } = useMemo(
    () =>
      getSimilarSashAndYMALProducts(
        products,
        true,
        isPLAUser ? colourId : undefined,
        sashCount
      ),
    [products, isPLAUser, colourId, sashCount]
  );

  useEffect(() => {
    if (isPLAUser && colourMatchesCount !== undefined) {
      sendActivityTrackerEvent(
        ActivityTrackerEventType.PRODUCT_RECOMMENDATIONS_LOADED_ACTION,
        {
          productId,
          anyCount: 0,
          brandCount: 0,
          colourCount: colourMatchesCount,
          categoryCount: 0,
          schemaVersion: '1.0',
          moduleName: 'SimilarItemsSash',
          totalCount: similarSashProducts.length,
        }
      );
    }
  }, [
    colourMatchesCount,
    sendActivityTrackerEvent,
    isPLAUser,
    productId,
    similarSashProducts.length,
  ]);

  function handleProductClick(productId: number) {
    sendProductInteractionActionATEvent(
      productId,
      interactionTypes.PRODUCT_SIMILAR_ITEMS
    );
  }

  return (
    <div className={styles.similarItemsSashWrapper}>
      <ModuleHeader title={title} size={ModuleHeaderSize.Small} />
      <ScrollableItemRow
        itemsPerRow={similarSashProducts.length}
        className={clsx(styles.scrollableItemRow, {
          [styles.scrollableItemRowVariant]: isVariantSash,
        })}
      >
        {similarSashProducts.map((product) => {
          const { display_price: displayPrice } = product.pricing;

          return (
            <li key={product.id}>
              <ProductCard
                productHref={`${product.slug}`}
                preview={picturePreviewAdapter(product.pictures?.[0])}
                handleClick={() => handleProductClick(product.id)}
                Overlay={() => (
                  <ProductCardLike
                    productId={product.id}
                    isLiked={product.is_liked}
                  />
                )}
                ProductAttributes={() => (
                  <div className={styles.attributesContainer}>
                    <Body
                      variant="sm-regular"
                      aria-description="Product brand name"
                    >
                      {product.brand_name || t('Other')}
                    </Body>
                    <Body variant="sm-regular" aria-description="Product size">
                      {getSizeString({
                        sizes: product.sizes,
                        multipleSizesString: t('MultipleSizes'),
                      })}
                    </Body>
                    <Price
                      inPreview
                      currencyCode={
                        displayPrice.headline_price.currency as CurrencyCode
                      }
                      price={
                        displayPrice.strikethrough_price?.amount ??
                        displayPrice.headline_price.amount
                      }
                      discountedPrice={
                        displayPrice.strikethrough_price
                          ? displayPrice.headline_price.amount
                          : undefined
                      }
                    />
                    {displayPrice.secondary_price && (
                      <SecondaryPriceLine
                        secondaryPrice={displayPrice.secondary_price}
                      />
                    )}
                  </div>
                )}
              />
            </li>
          );
        })}
      </ScrollableItemRow>
    </div>
  );
}
